import React from 'react';
import TriangleDownIcon from '@assets/icons/triangle-down.svg';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, TYPOGRAPHY } from '@/styles';
import {
  StyleProp,
  Text,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { useLayoutContext } from '@/contexts/layout-context';

const HIT_SLOP = {
  bottom: 8,
  left: 8,
  right: 8,
  top: 8,
};

export interface SelectorButtonProps {
  buttonTheme?: 'dark' | 'light';
  disabled?: boolean;
  onPress: () => void;
  style?: StyleProp<ViewStyle>;
  subtitle?: string;
  testId?: string;
  title: string;
  variant?: 'primary' | 'secondary';
  viewRef?: React.RefObject<View | null>;
}

export const SelectorButton = ({
  title,
  subtitle,
  onPress,
  viewRef,
  disabled,
  testId = 'selector-button',
  style,
  buttonTheme = 'dark',
  variant = 'primary',
}: SelectorButtonProps) => {
  const [styles, theme] = useStyles();
  const { isSmallLayout } = useLayoutContext();

  return (
    <TouchableOpacity
      disabled={disabled}
      hitSlop={HIT_SLOP}
      onPress={onPress}
      style={styles.shrinkable}
      testID={testId}
    >
      <View
        ref={viewRef}
        style={[
          variant === 'primary'
            ? styles.base
            : [styles.baseSecondary, disabled && styles.disabled],
          style,
        ]}
      >
        <View
          style={[
            variant === 'primary' ? styles.base : styles.baseButton,
            isSmallLayout && styles.button,
          ]}
        >
          <View
            style={[styles.shrinkable, Boolean(subtitle) && styles.baseText]}
          >
            <Text
              numberOfLines={1}
              style={[
                TYPOGRAPHY.h4,
                styles.buttonText,
                buttonTheme === 'light' ? styles.buttonTextLight : null,
                isSmallLayout ? null : styles.buttonTextLarge,
              ]}
            >
              {title}
            </Text>
            {subtitle ? (
              <Text
                numberOfLines={1}
                style={[TYPOGRAPHY.listSubtitle, styles.addressText]}
              >
                {subtitle}
              </Text>
            ) : null}
          </View>
          {disabled ? null : (
            <View style={styles.arrow}>
              <TriangleDownIcon
                fill={
                  buttonTheme === 'dark'
                    ? theme.colors.primary
                    : theme.colors.white
                }
              />
            </View>
          )}
        </View>
      </View>
    </TouchableOpacity>
  );
};

const useStyles = createStyles((theme) => ({
  addressText: {
    lineHeight: 20,
  },
  arrow: {
    marginLeft: 4,
  },
  base: {
    alignItems: 'center',
    flexDirection: 'row',
    flexShrink: 1,
    justifyContent: 'center',
    paddingHorizontal: 8,
  },
  baseButton: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  baseSecondary: {
    borderColor: theme.colors.stroke,
    borderRadius: 4,
    borderWidth: 1,
    marginBottom: 16,
    padding: 16,
    width: '100%',
  },
  baseText: {
    flex: 1,
  },
  button: {
    paddingHorizontal: 8,
  },
  buttonText: {
    ...FONTS.primaryBold,
    flexShrink: 1,
    lineHeight: 24,
  },
  buttonTextLarge: {
    fontSize: 16,
    marginRight: 4,
    maxWidth: 400,
  },
  buttonTextLight: {
    color: theme.colors.white,
  },
  disabled: {
    opacity: 0.4,
  },
  shrinkable: {
    flexShrink: 1,
  },
}));
